import {
  ArrowDownTrayIcon,
  BanknotesIcon,
  ClockIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  PlusIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import { setLoanData } from 'actions'
import cloneDeep from 'clone-deep'
import {
  DrawAdvisoryPanel,
  drawInspectionOptions,
  inspectionKeyOfRow,
  inspectionOptionKey,
  parseInspectionOptionKey,
  useDrawAdvisory,
} from 'components/DrawAdvisory'
import {
  drawAdvanceAmount,
  DrawAllocation,
  DrawFundedBy,
  DrawParticipant,
  participantKey,
  splitByOwnership,
  toAllocations,
} from 'components/DrawFundedBy'
import { feeSplitSummary } from 'components/FeeSplits'
import { LayoutLoading } from 'components/LayoutLoading'
import { ChosenPayee, PartyPayeeLookup } from 'components/PartyPayeeLookup'
import {
  confirmSufficientFunds,
  CONSTRUCTION_LEDGER_DEFAULT_SORT_KEY,
  CONSTRUCTION_LEDGER_ROWS_PER_PAGE,
  LEDGER_DEFAULT_SORT_ORDER,
  ledgerMoney,
} from 'components/TrustLedgerGrid'
import { BaseFile, baseUrl, InputType } from 'config'
import { useBankNameLookup } from 'hooks/useBankNameLookup'
import { resolveAccountGroup } from 'pages/Admin/Accounting/Accounts/types'
import { getAccountOptions } from 'pages/Admin/Accounting/Accounts/utils'
import { fundingDisbursementInputs } from 'pages/Loan/Funding'
import { type Payee, type Suggestion, AddPayableModal } from 'pages/Loan/Servicing/PartyPayments/AddPayableModal'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  addTrustLedger,
  deleteTrustLedger,
  getAccountingAccount,
  getBudgetTemplate,
  getGlobalDrawParticipants,
  getLoanPayables,
  getLoanPayees,
  getLoanPayments,
  getTrustLedger,
  getTrustLedgerCreditLine,
  getTrustLedgerInterest,
  getTrustLedgerOrderHistory,
  openS3Document,
  trustLedgerDrawIssuedAccountingTask,
  updateTrustLedger,
  updateTrustLedgerAdditional,
  updateTrustLedgerDrawType,
} from 'services'
import { FormTable, Input2, Modal, Tooltip } from 'stories/components'
import {
  confirm,
  formatDate,
  formatDateYMD,
  formatTime,
  getFileName,
  getPrice2decimal,
  getPrice3decimal,
  InputConvert,
  InputValidate,
  Num,
  openAuditLog,
  removeComma,
  solveDecimalJavascriptSum,
  thousandSeperatorWithPrefix,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { DRAW_TYPE_OPTIONS, normalizeDrawType } from '../../Budget/constants'
import { confirmActivePayoff } from '../payoffGuard'
import { AfxModal } from './AfxModal'
import { ChooseDocumentsForTrustLedger } from './ChooseDocumentsForTrustLedger'
import { additionalInputs, defaultInputs, header } from './constants'
import { ConstructionLienWaiver } from './ConstructionLienWaiver'
import { applyDrawCalculation } from './drawCalculation'
import { DrawFeeSplitDialog } from './DrawFeeSplitDialog'
import { DrawRequestApprovalModal } from './DrawRequestApprovalModal'
import { isOpeningBalanceRow, openingBalanceSeed } from './openingBalance'
import { buildReserveTiles } from './reserveBalances'
import { SendDrawApprovalModal } from './SendDrawApprovalModal'

/**
 * What a payment raised from a ledger row is stamped with. The backend reads the pair
 * (`draw`, the row's id) as the row itself being paid: its payment suggestions stop offering the
 * draw once a live payable covers it, and paying the check posts nothing further to the ledger
 * because this row is already the transaction.
 */
const LEDGER_ROW_SOURCE = 'draw'

/** The check-queue status a row can be in, weakest first - a live payment outranks a voided one. */
const PAYABLE_RANK: Record<string, number> = { voided: 0, queued: 1, paid: 2 }

export default function ConstructionReserveTab() {
  const dispatch = useDispatch()
  const token = useSelector((state: any) => state.auth.token)
  const { vendor } = useSelector((state: any) => ({
    vendor: state.vendor,
  }))
  const [loading, setLoading] = useState(false)
  const [initData, setInitData] = useState<Record<string, any>>({})
  const [data, setData] = useState<Array<Record<string, any>>>([])
  const [additionalData, setAdditionalData] = useState<Record<string, any>>({})
  const [isModalOpen, setModalOpen] = useState(false)
  const [documents, setDocuments] = useState<BaseFile[]>([])
  const [emailID, setEmailID] = useState(-1)
  const [inputs, setInputs] = useState<Record<string, InputType>>(additionalInputs())
  const { fillBankNameInput } = useBankNameLookup()
  const [fieldValueChanged, setFieldValueChanged] = useState(false)
  const [history, setHistory] = useState({
    id: null,
    data: [],
  })
  const [isAfxOpen, setAfxOpen] = useState(false)
  const [loanPaymentRows, setLoanPaymentRows] = useState<Array<Record<string, any>>>([])
  const [interestTrustLedgerRows, setInterestTrustLedgerRows] = useState<Array<Record<string, any>>>([])
  const [drawType, setDrawType] = useState('')
  const [budgetId, setBudgetId] = useState<number | null>(null)
  /** The borrower's draw requests on this loan, each carrying the status the backend resolved. */
  const [drawRequests, setDrawRequests] = useState<Record<string, any>[]>([])
  /**
   * The request the open form is being filled in for, so the row it saves is stamped with it.
   * Held here rather than as a form field because it is a link, not something anyone types; it is
   * cleared whenever a plain Add is started so a cancelled prefill cannot attach to the next row.
   */
  const [linkedRequestId, setLinkedRequestId] = useState<number | null>(null)
  /** The request whose line-by-line approval is open, if any. */
  const [approvingRequest, setApprovingRequest] = useState<Record<string, any> | null>(null)
  /** The request whose approval is being sent to the borrower, if any. */
  const [sendingApproval, setSendingApproval] = useState<Record<string, any> | null>(null)
  const [payables, setPayables] = useState<Array<Record<string, any>>>([])
  const [payees, setPayees] = useState<Payee[]>([])
  /**
   * Who can fund a draw on this loan, and who it is being paid to. Both are needed only once the
   * draw form is opened, so they load alongside the payees rather than on every page view.
   */
  const [participants, setParticipants] = useState<DrawParticipant[]>([])
  const drawPayee = useRef<ChosenPayee | null>(null)
  /** Set once a share is typed over, so re-running the calculation stops rewriting the split. */
  const allocationTouched = useRef(false)
  /** The row the draw form is open on, so the edited-by-hand flag does not leak to the next one. */
  const allocationRowId = useRef<number | undefined>(undefined)
  const [payPrefill, setPayPrefill] = useState<Partial<Suggestion> | null>(null)
  // The Construction Reserve row whose inspection fee is being split, or null when nothing is open.
  const [feeSplitRow, setFeeSplitRow] = useState<Record<string, any> | null>(null)
  // Chart of Accounts → Bank section options for the Construction Reserve entry modal.
  const [bankAccountOptions, setBankAccountOptions] = useState<Record<string, string>>({})
  // A revolving line of credit's position. Null for a closed-end loan, which is every loan without
  // a Facility Type, and the construction figures below are what that loan shows.
  const [creditLine, setCreditLine] = useState<Record<string, any> | null>(null)
  // The loan's payees are only needed once someone actually raises a payment, and asking again on
  // every click would refetch a list that does not change while the screen is open.
  const payeesLoaded = useRef(false)
  // Which row the Inspection field was last seeded for, so editing one draw does not carry its
  // inspection over to the next (the same reason the allocation split tracks its own row).
  const inspectionRowId = useRef<number | undefined>(undefined)
  const loanNumber = setLoanNumber()

  /**
   * The loan's draw inspections and every reason a servicer would normally refuse a draw. Shown
   * above the ledger and offered as a field on the entry form; it gates nothing (see
   * `components/DrawAdvisory`).
   */
  const { advisory } = useDrawAdvisory(loanNumber)
  const inspectionOptions = useMemo(() => drawInspectionOptions(advisory?.inspections || []), [advisory])
  const defaultInspectionKey = advisory?.latest ? inspectionOptionKey(advisory.latest) : ''

  const init = () => {
    setLoading(true)
    Promise.all([
      getTrustLedger(),
      getLoanPayments().catch(() => []),
      getTrustLedgerInterest().catch(() => ({} as Record<string, any>)),
      getLoanPayables().catch(() => null),
      getAccountingAccount().catch(() => []),
      getTrustLedgerCreditLine().catch(() => null),
      // Who would fund a draw on this loan. Small, and needed the moment the draw form opens, so
      // it rides along rather than being fetched on the click.
      getGlobalDrawParticipants(loanNumber).catch(() => null),
    ])
      .then(([data, loanPayments, interestRes, payablesRes, accounts, creditLineRes, participantRes]) => {
        setParticipants(Array.isArray((participantRes as any)?.participants) ? (participantRes as any).participants : [])
        // Only a revolving line is held; everything else keeps the construction reading.
        setCreditLine((creditLineRes as any)?.revolving ? (creditLineRes as any) : null)
        setData(data.data)
        setInitData(data)
        initInputs(data.additional, !!data.interestTypeFromServicing)
        setAdditionalData(data.additional)
        setDrawType(normalizeDrawType(data.drawType) || '')
        setBudgetId(data.budgetId || null)
        refreshDrawRequests(data.budgetId || null)
        setLoanPaymentRows(Array.isArray(loanPayments) ? loanPayments : [])
        setInterestTrustLedgerRows(interestRes?.interestData ?? [])
        setPayables(Array.isArray((payablesRes as any)?.items) ? (payablesRes as any).items : [])
        const bankAccounts = (Array.isArray(accounts) ? accounts : []).filter(
          (account) =>
            !account.invalid && resolveAccountGroup(account.section, account.type, account.statement) === 'Bank',
        )
        setBankAccountOptions(getAccountOptions(bankAccounts))
      })
      .finally(() => setLoading(false))
  }

  /**
   * The loan's draw requests, reread whenever the ledger is. They arrive on the budget payload
   * already carrying their status, which the backend resolves from this very ledger - so a draw
   * entered here moves the request on its own, with nothing to keep in step by hand.
   */
  const refreshDrawRequests = (id: number | null) => {
    if (!id) {
      setDrawRequests([])
      return
    }
    getBudgetTemplate(id)
      .then((res: any) => setDrawRequests(Array.isArray(res?.drawRequests) ? res.drawRequests : []))
      .catch(() => setDrawRequests([]))
  }

  /** Two decimals, like every other figure on this screen. */
  const money = (value: number) => `$${getPrice2decimal(value)}`

  /** What a request asked for in total: its money is on the line entries, not on the request. */
  const drawRequestTotal = (request: Record<string, any>) =>
    (request?.types || []).reduce((sum: number, line: any) => sum + Num(line?.amount), 0) || Num(request?.amount)

  /**
   * What the lender has approved on a request, where a decision stands.
   *
   * The backend works the total out and sends it, so this is only the fallback for a payload from
   * before it did. Null, not zero, when nothing has been approved: the two read very differently
   * under a column headed "Approved".
   */
  const drawRequestApproved = (request: Record<string, any>): number | null => {
    if (!request?.approval?.current) return null
    if (typeof request?.approvedTotal === 'number') return request.approvedTotal
    return (request.approval.current.lines || []).reduce((sum: number, line: any) => sum + Num(line?.amount), 0)
  }

  /**
   * How far the borrower's copy of the decision has got.
   *
   * Read off the decision itself rather than stored beside it, so revising the figures resets it -
   * which is the point: a borrower who acknowledged one set of amounts has not acknowledged the
   * set that replaced them.
   */
  const drawApprovalState = (request: Record<string, any>): 'none' | 'approved' | 'sent' | 'acknowledged' => {
    const current = request?.approval?.current
    if (!current) return 'none'
    if (!current.documentation?.sentAt) return 'approved'
    return current.documentation.acknowledgedAt ? 'acknowledged' : 'sent'
  }

  /** The lines it was asked against, for the one-line summary under each request. */
  const drawRequestLines = (request: Record<string, any>) =>
    (request?.types || [])
      .map((line: any) => [line?.desc, money(Num(line?.amount))].filter(Boolean).join(' - '))
      .filter(Boolean)
      .join(' · ') || request?.note || ''

  /**
   * Requests still waiting on a draw - the only ones this panel offers to act on.
   *
   * Two conditions, because a request leaves this panel two different ways. A draw entered
   * against it stamps `trustLedgerId`, and from then on the ledger row is its status. Closing it
   * without funding stamps `closedAs` ('declined' when the lender turned it down, 'withdrawn'
   * when the borrower took it back) and there is no row to read, so the request's own closure is
   * the whole story - see the backend's `draw-request-status`. Filtering on the row alone left
   * every declined and withdrawn request sitting here under "waiting for a draw", with a Create
   * draw button offering to fund something nobody agreed to.
   */
  /** The request the open draw form was started from, so the form can say what it is answering. */
  const linkedDrawRequest = useMemo(
    () => (linkedRequestId ? drawRequests.find((request) => Number(request?.id) === linkedRequestId) || null : null),
    [drawRequests, linkedRequestId],
  )

  const pendingDrawRequests = useMemo(
    () => drawRequests.filter((request) => !request?.trustLedgerId && !request?.closedAs),
    [drawRequests],
  )

  /**
   * What the draw being entered is answering: every line of the request, requested beside
   * approved, in front of the servicer while they fill the form in.
   *
   * The money fields are deliberately NOT prefilled from any of it. Gross Eligible is the
   * cumulative cost to date and the servicer's own determination, which no payment request can
   * supply - see `createDrawFromRequest`. This is the evidence, not the answer.
   */
  const renderDrawRequestComparison = (request: Record<string, any>) => {
    const approved = drawRequestApproved(request)
    return (
      <div className="mb-4 rounded border border-shade-blue/30 bg-shade-blue/5 px-4 py-3">
        <div className="font-variation-settings-600 text-[15px] mb-2">Draw request being funded</div>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left opacity-70">
              <th className="py-1">Line</th>
              <th className="py-1 text-right w-28">Requested</th>
              <th className="py-1 text-right w-28">Approved</th>
              <th className="py-1 pl-4">Reason</th>
            </tr>
          </thead>
          <tbody>
            {(request.approvalLines || []).map((line: any, index: number) => (
              <tr key={index}>
                <td className="py-1">{line.desc || line.key}</td>
                <td className="py-1 text-right whitespace-nowrap">{money(Num(line.requested))}</td>
                <td className="py-1 text-right whitespace-nowrap">
                  {line.approved === null ? (
                    <span className="opacity-60">not approved</span>
                  ) : (
                    money(Num(line.approved))
                  )}
                </td>
                <td className="py-1 pl-4 opacity-70">{line.reason || ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-variation-settings-600">
              <td className="py-1">Total</td>
              <td className="py-1 text-right whitespace-nowrap">{money(drawRequestTotal(request))}</td>
              <td className="py-1 text-right whitespace-nowrap">{approved === null ? '-' : money(approved)}</td>
              <td className="py-1 pl-4" />
            </tr>
          </tfoot>
        </table>
        {approved === null && (
          <div className="text-[13px] mt-2 text-yellow-800">
            Nobody has approved this request line by line yet.
          </div>
        )}
      </div>
    )
  }

  /** The next DRAW-n, following whatever the loan already uses rather than inventing a scheme. */
  const nextDrawReference = () => {
    const used = data
      .map((row) => /^DRAW-(\d+)$/i.exec(String(row?.reference || '').trim()))
      .filter(Boolean)
      .map((match) => Number((match as RegExpExecArray)[1]))
    return `DRAW-${used.length ? Math.max(...used) + 1 : 1}`
  }

  /**
   * Open the entry form filled in from a borrower's request.
   *
   * Only the facts the request carries are prefilled - who it came from, what the borrower said
   * about the work, and the lines and amounts it was asked against, which go into the notes. Not
   * one of the money fields: every figure on this form is the lender's own determination, and the
   * request is evidence towards them rather than an answer to any of them.
   *
   * It used to seed Gross Eligible with the request's total, which is a different quantity -
   * Gross is the CUMULATIVE cost to date, and the chain nets Previously Disbursed off it. On a
   * loan with earlier draws that opened the form on a large negative New Eligible before the
   * servicer had typed anything: $110,000 asked against $801,871.08 already disbursed read as
   * -$699,371.08. Nor would adding Previously Disbursed back on have fixed it, because the draw
   * type scales Gross before the subtraction, so the figure would have missed the request anyway -
   * on that loan it would have landed on $47,965.29 rather than the $110,000 asked for. There is
   * no honest way to derive a cost-to-date from a payment request, so the servicer enters it from
   * the draw package and `applyDrawCalculation` leaves the derived fields blank until then.
   */
  const createDrawFromRequest = async (request: Record<string, any>) => {
    const narrative = [request?.note, drawRequestLines(request)].filter(Boolean).join(' | ')

    setLinkedRequestId(Number(request.id))
    await formTableRef.current?.openAddWith({
      type: 'Draw',
      // `formatDate` takes LUXON tokens, and 'YYYY-MM-DD' is not them: Y is passed through
      // literally and D is the localized date, so this produced 'YYYY-10-Oct 12, 2026', which
      // `InputConvert` then parsed as a date and got null from - the field opened empty, on a
      // required field, every time. `formatDateYMD` is the helper for this and is what the rest
      // of the repo uses.
      date: formatDateYMD(),
      reference: nextDrawReference(),
      fromWhom: request?.contactName || `${request?.firstName || ''} ${request?.lastName || ''}`.trim(),
      notes: narrative,
      files: request?.files || [],
    })
  }

  /** The loan's check queue on its own, reread after raising a payment so the row says so. */
  const refreshPayables = () => {
    getLoanPayables()
      .then((res: any) => setPayables(Array.isArray(res?.items) ? res.items : []))
      .catch(() => {})
  }

  const {
    fundingInfo = {},
    rehabBudget = -1,
    constructionReserve = -1,
    interestReserve = -1,
    chargeInterestOnReserve = false,
    proposedLoanAmount,
    initialLoanAmount,
    actualInitialLoanAmount,
    afterRepairValue,
  } = initData

  useEffect(() => {
    setLoading(true)
    init()
  }, [])

  const initInputs = (additionalData: Record<string, any>, interestTypeFromServicing = false) => {
    let newInputs = cloneDeep(inputs)
    Object.keys(additionalData).forEach((key) => {
      if (newInputs[key]) newInputs[key].value = additionalData[key]
    })
    // `additional.interestType` now arrives resolved over every store that can hold one, the
    // servicing record first. On a loan boarded from another servicer that is the only store with
    // a type in it, which is why this select used to sit on `- Select -` beside a Servicing tab
    // reading Dutch, and why the tiles below it answered Non Dutch on a Dutch loan.
    //
    // When the servicing record is the store holding it, the value shows and the select locks:
    // this select writes the ledger row, which the servicing record outranks on the next read, so
    // an edit here would look saved and come back unchanged. Servicing Information owns the field,
    // where it is permission-gated and logged. A loan nobody has serviced keeps the select
    // editable, which is how it is set at origination.
    if (interestTypeFromServicing && newInputs.interestType) {
      newInputs.interestType.disabled = true
      newInputs.interestType.tooltip = 'Set on the Servicing Information screen, under Interest Calculations.'
    }
    newInputs = updateInputs(newInputs)
    setInputs(newInputs)
  }

  const showOrderHistory = async (id: number) => {
    setLoading(true)
    const res = await getTrustLedgerOrderHistory(id)
    if (res.success) {
      setHistory(res.data)
    }
    setLoading(false)
  }

  const dataWithBalance = useMemo(() => {
    let balance = constructionReserve === -1 ? 0 : openingBalanceSeed(constructionReserve, data)
    let calcBalanceSum = 0
    // Build a new row for each entry rather than writing back onto `data`. This memo used to
    // mutate the rows it was given and return the same array: it derived grossEligibleDrawAmount
    // from previouslyDisbursed and then overwrote previouslyDisbursed with the running total, so
    // running it a second time over the same rows computed the gross amount from the total the
    // first run had written, and the draw figures drifted.
    return data.map((row) => {
      const item: Record<string, any> = { ...row }
      item.ID = (
        <span
          className="cursor-pointer hover:underline font-semibold flex items-center hover:text-shade-blue"
          onClick={() => showOrderHistory(item.id)}
        >
          <span className="text-shade-blue">{item.id}</span>
          <ClockIcon className="w-3 h-3 ml-1"></ClockIcon>
        </span>
      )
      item.referenceWithFrom = (
        <span>
          <div className="border-b w-full mb-2">{item.reference}</div>
          {item.fromWhom}
        </span>
      )
      item.eligibleWithNetAmount = (
        <span>
          <div className="border-b w-full mb-2">{ledgerMoney(item.eligibleAmount)}</div>
          {ledgerMoney(item.netEligibleAmount)}
        </span>
      )
      item.paymentWithDeposit = (
        <span>
          <div className="border-b w-full mb-2">{ledgerMoney(item.payment)}</div>
          {ledgerMoney(item.deposit)}
        </span>
      )
      item.fee = getPrice3decimal(
        solveDecimalJavascriptSum([
          removeComma(item.inspectionFee),
          removeComma(item.wireFee),
          removeComma(item.titleUpdateFee),
        ]),
      )
      item.feeWithAddHoldback = (
        <span>
          <div className="border-b w-full mb-2">{ledgerMoney(item.fee)}</div>
          {ledgerMoney(item.additionHoldback)}
        </span>
      )

      balance = solveDecimalJavascriptSum([
        removeComma(balance),
        -1 * removeComma(item.payment),
        removeComma(item.deposit),
        -1 * removeComma(item.fee),
        -1 * removeComma(item.additionHoldback),
      ])
      item.balance = getPrice3decimal(balance)
      item.fileCount = item.files ? item.files.length : 0
      if (constructionReserve === -1) {
        item.recommendedDisbursement = 0
      } else {
        item.recommendedDisbursement = getPrice3decimal(
          solveDecimalJavascriptSum([
            removeComma(item.netEligibleAmount),
            -1 * calcBalanceSum,
            -1 * removeComma(item.fee),
            -1 * removeComma(item.additionHoldback),
          ]),
        )
      }
      item.grossEligibleDrawAmount = getPrice3decimal(
        solveDecimalJavascriptSum([removeComma(item.netEligibleAmount), -1 * removeComma(item.previouslyDisbursed)]),
      )
      item.previouslyDisbursed = calcBalanceSum
      // The opening deposit funds the reserve, it is not a disbursement, so it must not
      // count against cumulative-disbursed (it would otherwise drive this negative).
      if (!isOpeningBalanceRow(item)) {
        calcBalanceSum = solveDecimalJavascriptSum([
          calcBalanceSum,
          removeComma(item.payment),
          -1 * removeComma(item.deposit),
          removeComma(item.fee),
          removeComma(item.additionHoldback),
        ])
      }

      item.drawWithBalance = (
        <span>
          <div className="border-b w-full mb-2">
            {ledgerMoney(
              solveDecimalJavascriptSum([
                removeComma(item.payment),
                removeComma(item.deposit),
                removeComma(item.fee),
                removeComma(item.additionHoldback),
              ]),
            )}
          </div>
          {ledgerMoney(item.balance)}
        </span>
      )
      item.dateWithFiles = (
        <span>
          <div className="border-b w-full mb-2">{formatDate(item.date)}</div>
          {item.fileCount}
        </span>
      )
      return item
    })
  }, [data, constructionReserve])

  /**
   * The ledger draws a lien waiver can be raised against.
   *
   * Draws only: a deposit funds the reserve and the opening balance is the book the loan arrived
   * with, and neither is money advanced to a claimant. The amount offered is what the row paid
   * out, NOT its Gross Eligible Amount - that figure is cumulative cost to date, and it is what
   * the picker used to copy into the waiver.
   */
  const lienWaiverDraws = useMemo(
    () =>
      dataWithBalance
        .filter((row: any) => Number(row?.id) > 0 && !isOpeningBalanceRow(row) && removeComma(row.payment) > 0)
        .map((row: any) => ({
          source: 'ledger' as const,
          id: Number(row.id),
          reference: [row.date, row.reference, row.fromWhom].filter(Boolean).join(' / ') || `Draw ${row.id}`,
          drawnAmount: removeComma(row.payment),
        })),
    [dataWithBalance],
  )

  /**
   * The reserve that has not been drawn yet - the closing figure of the walk above, or the whole
   * reserve while nothing has been entered. This is what the Remaining Construction Reserve tile
   * shows and what a new entry has to come out of, so both read it from here.
   */
  const constructionBalance = useMemo(() => {
    const len = dataWithBalance.length
    // No ledger rows yet -> full reserve remains (matches backend getConstructionReserveBalance).
    return len > 0 ? dataWithBalance[len - 1].balance : constructionReserve === -1 ? 0 : constructionReserve
  }, [dataWithBalance, constructionReserve])

  const formTableRef = useRef<any>(null)

  // Warn before adding a ledger entry against an active payoff. The same guard runs on every
  // other screen that posts a transaction to the loan - see payoffGuard.
  const onAddPress = async () => confirmActivePayoff(additionalData['payoffDate'])

  const onRemove = async (id: any) => {
    return new Promise((resolve) => {
      setLoading(true)
      deleteTrustLedger(id).then(() => {
        init()
        resolve(true)
      })
    })
  }

  /**
   * What an entry takes out of the reserve, net of anything it puts back. Mirrors the balance walk
   * in `dataWithBalance`: fees and the additional holdback come out of the reserve alongside the
   * payment. A Transfer moves its whole amount out (the backend books it as the row's payment) and
   * a Deposit puts money in, so it nets negative and can never read as a withdrawal.
   */
  const netWithdrawal = (row: Record<string, any> | null | undefined) => {
    if (!row) return 0
    return solveDecimalJavascriptSum([
      removeComma(row.type === 'Transfer' ? row.amount : row.payment),
      removeComma(row.inspectionFee),
      removeComma(row.wireFee),
      removeComma(row.titleUpdateFee),
      removeComma(row.additionHoldback),
      -1 * removeComma(row.deposit),
    ])
  }

  const onSubmit = async (currentId: any, values: Record<string, any>) => {
    // A revolving line is not asked about. Its limit is enforced on the server, which refuses the
    // advance outright rather than offering to continue, so there is no question to put here - and
    // the construction reserve balance this would measure against is the wrong number for a line
    // anyway: it goes negative the first time the line is repaid and re-drawn. Leaving the rule in
    // one place also keeps the two from drifting apart.
    if (!creditLine) {
      // Editing a row: what it already takes out is added back, so the check measures the change.
      const edited = currentId ? data.find((row: Record<string, any>) => row.id == currentId) : null
      const available = solveDecimalJavascriptSum([removeComma(constructionBalance), netWithdrawal(edited)])
      // Cancelling has to leave the form open with what was typed still in it, which is what
      // throwing does - FormTable swallows it. Resolving would close the row as if it had saved.
      if (!(await confirmSufficientFunds(netWithdrawal(values), available, 'Construction Reserve')))
        throw new Error('cancelled')
    }

    // A new row raised from a borrower's request carries its id, which is what lets the request
    // report where it got to. Only on create: re-saving an existing row must not re-point it.
    const payload: Record<string, any> =
      currentId == 0 && linkedRequestId ? { ...values, drawRequestId: linkedRequestId } : { ...values }

    // The Inspection field holds one `source:id` key; the row stores the pair. Splitting it here
    // keeps the composite out of the row's jsonb - the validation pipe has no whitelist, so an
    // undeclared field would be saved silently and nothing would say it was part of the record.
    const inspection = parseInspectionOptionKey(payload.inspectionOrder)
    delete payload.inspectionOrder
    payload.inspectionOrderId = inspection?.inspectionOrderId
    payload.inspectionOrderSource = inspection?.inspectionOrderSource

    return new Promise((resolve, reject) => {
      setLoading(true)
      const save = currentId == 0 ? addTrustLedger(payload) : updateTrustLedger(currentId, payload)
      save
        .then(() => {
          setLinkedRequestId(null)
          init()
          resolve(values)
        })
        .catch((err: any) => {
          // A refused advance (a line over its credit limit, a closed draw period) comes back 400
          // with the reason, which Api has already toasted. Rejecting leaves the row open with
          // what was typed so the amount can be corrected; without a catch here the save never
          // settled and the form sat on its spinner with nothing said.
          setLoading(false)
          reject(err)
        })
    })
  }

  const onExportFile = (type: 'excel' | 'pdf') => {
    const url = `${baseUrl}/loan/downloadTrustLedger/${loanNumber}?token=${token}&type=${type}`

    var windowReference: any = window.open()
    windowReference.document.title = 'Downloading...'
    windowReference.location = url
  }

  const renderRehabBudgetFragment = useMemo(() => {
    // Total Draw Disbursed counts only trust-ledger transactions of type 'Draw'
    // (excludes Transfers), matching how the reserve balance is drawn down per row.
    // The summary figures are struck by `buildReserveTiles`, shared with the pipeline's Draw
    // modal so the balances read the same wherever a servicer looks at this loan. The raw ledger
    // rows go in rather than `dataWithBalance`, which carries this grid's JSX.
    const paidPrincipal = loanPaymentRows.reduce((sum, p) => sum + Number(removeComma(p.principal) || 0), 0)
    const inputObjs: any = buildReserveTiles({
      rows: data,
      interestRows: interestTrustLedgerRows,
      rehabBudget,
      constructionReserve,
      interestReserve,
      initialLoanAmount,
      actualInitialLoanAmount,
      proposedLoanAmount: Number(proposedLoanAmount),
      afterRepairValue,
      chargeInterestOnReserve,
      interestType: inputs.interestType.value,
      paidPrincipal,
      // Held here as an untyped record straight off the endpoint; `buildReserveTiles` reads only
      // the five figures it declares, and keys the whole set off `revolving`.
      creditLine: creditLine as any,
    })

    const onChangeDrawType = async (_key: string, value: string) => {
      setDrawType(value)
      if (!budgetId) return
      setLoading(true)
      try {
        await updateTrustLedgerDrawType(value)
      } finally {
        setLoading(false)
      }
    }

    return (
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {inputObjs.map((obj: any, index: number) => {
          return (
            <div key={index}>
              <Input2
                key={obj[0]}
                title={obj[1]}
                value={obj[2]}
                prefix={obj[3]}
                type={'thousandSep'}
                disabled={true}
                onChange={() => {}}
              />
            </div>
          )
        })}
        <div>
          <RenderInput
            Key="drawType"
            input={{
              inputType: 'select',
              title: 'Draw Type',
              options: DRAW_TYPE_OPTIONS,
              value: drawType,
              hasDefaultOption: true,
              disabled: !budgetId,
            }}
            onChange={onChangeDrawType}
          />
        </div>
      </div>
    )
  }, [
    rehabBudget,
    constructionReserve,
    interestReserve,
    dataWithBalance,
    loanPaymentRows,
    interestTrustLedgerRows,
    proposedLoanAmount,
    actualInitialLoanAmount,
    chargeInterestOnReserve,
    inputs.interestType.value,
    drawType,
    budgetId,
    creditLine,
  ])

  /**
   * The entry form's fields, with the two controls this screen supplies itself.
   *
   * Passed to FormTable as a factory because that is what it calls when the form opens
   * (`openAdd`); `inputLogic` runs on edit and on every change but NOT on open, so a renderer
   * attached only there does not appear until the first keystroke.
   *
   * Memoized on the lists the renderers close over. The chosen payee is deliberately a ref rather
   * than state: as a dependency it would rebuild the form, and therefore the inputs, in the
   * middle of choosing.
   */
  const buildInputs = useCallback(() => {
    const inputs = defaultInputs()

    // Who is being paid comes from the loan's parties and the vendor book rather than being
    // typed, the same lookup the pipeline's Draw modal and Pay a Party use. Only the name reaches
    // the ledger row: the check is raised separately here, by Queue a Check, which resolves the
    // payee record itself.
    inputs.fromWhom.inputType = 'custom'
    ;(inputs.fromWhom as any).render = (value: any, onChange: Function) => (
      <PartyPayeeLookup
        title="From Whom Received/Paid"
        required
        parties={payees as any}
        value={
          drawPayee.current && drawPayee.current.name === value
            ? drawPayee.current
            : value
            ? { payeeType: '', payeeRefId: null, name: String(value), address: '' }
            : null
        }
        onSelect={(payee) => {
          drawPayee.current = payee
          onChange(payee?.name || '')
        }}
      />
    )

    // Which inspection a new draw is taken against, defaulted to the loan's newest live order.
    // `openAdd` does not run `inputLogic`, so a new row has to be seeded here or the field opens
    // empty on a loan that does have an inspection.
    ;(inputs.inspectionOrder as any).options = inspectionOptions
    inputs.inspectionOrder.value = defaultInspectionKey

    ;(inputs.allocations as any).render = (value: any, onChange: Function, input: any) => {
      const rows: DrawAllocation[] = Array.isArray(value) ? value : []
      const map = Object.fromEntries(rows.map((row) => [participantKey(row as any), String(row.amount)]))
      const amount = Number(input?.drawAmount) || 0
      return (
        <DrawFundedBy
          participants={participants}
          allocation={map}
          drawAmount={amount}
          touched={allocationTouched.current}
          onChange={(k, v) => {
            allocationTouched.current = true
            onChange(toAllocations(participants, { ...map, [k]: v }))
          }}
          onResetToOwnership={() => {
            allocationTouched.current = false
            onChange(toAllocations(participants, splitByOwnership(participants, amount)))
          }}
        />
      )
    }

    return inputs
  }, [payees, participants, inspectionOptions, defaultInspectionKey])

  const inputLogic = (inputs: Record<string, InputType>, ID?: number, key?: string) => {
    // A different row (or a new one) is a fresh split: without this, editing a draw whose shares
    // were typed by hand would leave the next draw refusing to default itself.
    if (allocationRowId.current !== ID) {
      allocationRowId.current = ID
      allocationTouched.current = Array.isArray(inputs.allocations?.value) && inputs.allocations.value.length > 0
    }
    if (!inputs.type.value) inputs.type.value = 'Draw'
    // The inspection options come from the loan, not the row, so they are set on every pass. The
    // value is seeded once per row: the row stores the id/source pair, the field holds the key.
    ;(inputs.inspectionOrder as any).options = inspectionOptions
    if (inspectionRowId.current !== ID) {
      inspectionRowId.current = ID
      const row = ID ? data.find((item: Record<string, any>) => item.id == ID) : null
      inputs.inspectionOrder.value = row ? inspectionKeyOfRow(row) : defaultInspectionKey
    }
    if (inputs.bankAccountId) {
      ;(inputs.bankAccountId as any).options = bankAccountOptions
    }
    if (inputs.type.value === 'Transfer') {
      if (key === 'type') {
        inputs.payment.value = 0
        inputs.deposit.value = 0
        inputs.fromWhom.value = ''
        inputs.inspectionFee.value = 0
        inputs.wireFee.value = 0
        inputs.titleUpdateFee.value = 0
        inputs.additionHoldback.value = 0
      }
      return inputs
    }

    // The draw chain (previously disbursed -> net eligible -> recommended disbursement) lives in
    // `applyDrawCalculation`, shared with the pipeline's Draw modal so both screens compute a draw
    // the same way rather than from two copies of the rule.
    applyDrawCalculation(inputs, { rows: data, drawType, rehabBudget, constructionReserve, rowId: ID }, key)

    // The Funded by split follows the draw until a share is typed over, so it always states the
    // amount actually being drawn. The amount rides on the input itself because the renderer is
    // built once by `buildInputs` and cannot close over it.
    //
    // It is the draw's whole ADVANCE, not its payment: a draw charging a fee pays the contractor
    // less than it advances, and the investors fund the fee too (`drawAdvanceAmount`).
    if (inputs.type.value === 'Draw') {
      const amount = drawAdvanceAmount({
        payment: inputs.payment.value,
        deposit: inputs.deposit.value,
        inspectionFee: inputs.inspectionFee?.value,
        wireFee: inputs.wireFee?.value,
        titleUpdateFee: inputs.titleUpdateFee?.value,
        additionHoldback: inputs.additionHoldback?.value,
      })
      ;(inputs.allocations as any).drawAmount = amount
      if (!allocationTouched.current) inputs.allocations.value = toAllocations(participants, splitByOwnership(participants, amount))
    }
    return inputs
  }

  const onCloseTrustLedgerEmailModal = () => {
    setModalOpen(false)
    setDocuments([])
  }

  const updateInputs = (inputs: Record<string, InputType>) => {
    const isWiringInstructions = inputs.isWiringInstructions.value
    Object.keys(inputs).forEach((key) => {
      if (['isWiringInstructions', 'comment', 'interestComment', 'pledgedFacility', 'interestType'].indexOf(key) !== -1)
        return
      inputs[key].visible = isWiringInstructions
    })
    return inputs
  }

  const onChange = async (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)

    // Interest Type is a loan field the panel edits on the loan's behalf, so the store has to hear
    // about it or the rest of the app keeps quoting the old treatment.
    if (key === 'interestType') await dispatch(setLoanData({ key, data: value }))

    newInputs[key].value = value
    newInputs[key].error = InputValidate(newInputs[key])

    newInputs = updateInputs(newInputs)
    setInputs(newInputs)
    setFieldValueChanged(true)
    if (key === 'routingNumber') fillBankNameInput(newInputs, key, 'bankName', setInputs)
  }

  const onUpdate = async (key: string) => {
    if (!fieldValueChanged) return
    setFieldValueChanged(false)
    const input: any = inputs[key]
    if (input.error && input.error.length > 0) return
    setLoading(true)
    const reqBody = {
      [key]: inputs[key].value,
    }
    await updateTrustLedgerAdditional(reqBody)
    // Interest Type is a loan field that changes the backend-computed initial advance and Current
    // UPB, so re-fetch the panel to refresh the tiles instead of leaving them on the figures from
    // before the save. init() manages its own loading flag.
    if (key === 'interestType') {
      init()
      return
    }
    setLoading(false)
  }

  const onAdditionalShowHistory = (key: string) => {
    const options = {
      table: 'TrustLedger',
      field: key,
      keys: {
        field: key,
        loanNumber,
      },
    }
    openAuditLog(options)
  }

  const drawIssued = async (data: any) => {
    const content = (
      <div className="mb-4">
        <div className="mb-3">Are you sure want to Inform Subservicer of Draw?</div>
        <div className="">
          Order ID: <span className="font-bold">{data.id}</span>
        </div>
      </div>
    )
    const res = await confirm(content, {
      titleYes: 'Yes and Inform',
    })
    if (!res) return
    setLoading(true)
    await trustLedgerDrawIssuedAccountingTask({
      interestType: inputs.interestType.value,
      data: {
        id: data.id,
        reference: data.reference,
        drawIssued: data.drawIssued,
      },
    })
    init()
  }

  /**
   * The check queued or cut for each ledger row, keyed by that row's id. A row can carry more than
   * one over time - a voided check and then a real one - so the live payment wins over a voided
   * one, and a paid one over a queued one. Payables come back newest first, so ties keep the newest.
   *
   * Two ways a row and a payment belong together, and both have to be read or a row offers to pay
   * money that has already gone out. `sourceId` is a payment raised FROM this row; `trustLedgerId`
   * is the reverse - the row a paid payment WROTE when it posted to the ledger, which is every
   * party payment, invoice and voucher the check queue has ever paid on this loan.
   */
  const payableByRow = useMemo(() => {
    const byRow = new Map<number, Record<string, any>>()
    const link = (rowId: any, p: Record<string, any>) => {
      if (!(Number(rowId) > 0)) return
      const seen = byRow.get(Number(rowId))
      if (!seen || (PAYABLE_RANK[p.status] ?? 0) > (PAYABLE_RANK[seen.status] ?? 0)) byRow.set(Number(rowId), p)
    }
    payables.forEach((p) => {
      if (String(p?.sourceType) === LEDGER_ROW_SOURCE) link(p?.sourceId, p)
      link(p?.trustLedgerId, p)
    })
    return byRow
  }, [payables])

  /** What this row pays out to a payee: the disbursement itself, not the fees the lender keeps. */
  const payoutAmount = (row: Record<string, any>) => Number(removeComma(row?.payment)) || 0

  /**
   * Queue a check for what this row pays out. The row already says who was paid, how much and
   * against which draw, so the payment is confirmed rather than rekeyed on Party Payments. It
   * carries the row as its source, which is what keeps the two from stating the same money twice:
   * the payment suggestions stop offering this draw, and paying the check posts nothing new to the
   * ledger. Nothing leaves the trust account until the check is actually paid in the check queue.
   */
  const onQueueCheck = async (row: Record<string, any>) => {
    // The same guard every other screen that raises a transaction on the loan runs - see payoffGuard.
    if (!(await confirmActivePayoff(additionalData?.['payoffDate']))) return
    if (!payeesLoaded.current) {
      setLoading(true)
      const res: any = await getLoanPayees().catch(() => null)
      setPayees(Array.isArray(res?.payees) ? res.payees : [])
      payeesLoaded.current = true
      setLoading(false)
    }
    setPayPrefill({
      sourceType: LEDGER_ROW_SOURCE,
      sourceId: row.id,
      // No payeeType: the row only stores a name, and matching on a type it does not have would
      // drop a party who IS on the loan to free text. AddPayableModal matches on the name alone.
      payeeName: row.fromWhom || '',
      amount: payoutAmount(row),
      memo: row.memo || row.reference || `Draw ${formatDate(row.date)}`.trim(),
      // What the payout books under. A draw has to stay a draw; anything else keeps its own
      // category so the ledger reads the same way after the check as before it.
      ledgerCategory: row.type === 'Draw' ? 'Construction Draw' : row.subType || '',
    })
  }

  /**
   * Pay this row out by check. Blue offers it, amber says it is waiting in the check queue, green
   * says the check has been cut - a row that has already been raised must not quietly raise a
   * second payment for the same money.
   */
  const renderQueueCheckAction = (row: Record<string, any>) => {
    const payable = payableByRow.get(Number(row.id))

    if (payable?.status === 'paid') {
      const paidBy = [payable.paymentMethod || 'check', payable.referenceNumber].filter(Boolean).join(' ')
      return (
        <Tooltip
          message={`Paid ${thousandSeperatorWithPrefix(payable.amount)} to ${payable.payeeName}${
            paidBy ? ` by ${paidBy}` : ''
          }`}
        >
          <div className="text-green-600 p-1 rounded w-fit">
            <CurrencyDollarIcon className="w-4 h-4" />
          </div>
        </Tooltip>
      )
    }

    if (payable?.status === 'queued')
      return (
        <Tooltip
          message={`${thousandSeperatorWithPrefix(payable.amount)} to ${payable.payeeName} is in the check queue`}
        >
          <div className="text-amber-600 p-1 rounded w-fit">
            <CurrencyDollarIcon className="w-4 h-4" />
          </div>
        </Tooltip>
      )

    return (
      <Tooltip message="Queue a Check">
        <div
          className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded w-fit"
          onClick={() => onQueueCheck(row)}
        >
          <CurrencyDollarIcon className="w-4 h-4" />
        </div>
      </Tooltip>
    )
  }

  const renderAdditionalActions = (row: Record<string, any>) => {
    if (row.type === 'Transfer') return <></>

    const drawCN = row.drawIssued ? 'bg-green-500' : 'bg-red-500'

    return (
      <div className="flex gap-1">
        {!!inputs.interestType.value?.length && (
          <div>
            <Tooltip message={`Draw Issue${row.drawIssued ? 'd' : ''}`}>
              <div
                className={`text-white ${drawCN} cursor-pointer hover-shadow1 p-1 rounded w-fit`}
                onClick={() => {
                  !row.drawIssued && drawIssued(row)
                }}
              >
                <BanknotesIcon className="w-4 h-4"></BanknotesIcon>
              </div>
            </Tooltip>
          </div>
        )}
        <div
          className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded w-fit"
          onClick={() => {
            setModalOpen(true)
            setDocuments(row.files)
            setEmailID(row.id)
          }}
        >
          <EnvelopeIcon className="w-4 h-4" />
        </div>
        {/* A deposit puts money in and the fees stay with the lender, so only a row that pays
            someone can be paid out. */}
        {payoutAmount(row) > 0 && renderQueueCheckAction(row)}
        {renderFeeSplitAction(row)}
      </div>
    )
  }

  /**
   * Split this row's inspection fee between the people it is owed to (Smartsheet row 376).
   *
   * Offered only on a row that actually carries a fee: there is nothing to divide otherwise, and the
   * server refuses a split with no fee behind it. Amber once shares are set, so a servicer can see
   * at a glance which draws have a split without opening each one.
   */
  const renderFeeSplitAction = (row: Record<string, any>) => {
    if (!(Number(removeComma(row?.inspectionFee)) > 0)) return <></>
    const shares = Array.isArray(row?.inspectionFeeSplits) ? row.inspectionFeeSplits : []
    const summary = feeSplitSummary(shares)
    return (
      <Tooltip message={summary ? `Inspection fee split: ${summary}` : 'Split the inspection fee'}>
        <div
          className={`${summary ? 'text-amber-600' : 'text-shade-blue'} cursor-pointer hover-shadow1 p-1 rounded w-fit`}
          onClick={() => setFeeSplitRow(row)}
        >
          <UsersIcon className="w-4 h-4" />
        </div>
      </Tooltip>
    )
  }

  const renderDetail = (item: any) => {
    if (['Added', 'Updated'].includes(item.action))
      return <div className="max-w-md break-all">{JSON.stringify(item.info)}</div>

    let rlt: any = []

    if (['Draw Approved Email', 'Draw Review Request Email'].includes(item.action)) {
      item.info.data.map((item1: any, index: number) => {
        rlt.push(
          <div className="border p-1" key={index}>
            {item1.title}: <span>${getPrice2decimal(item1.value, false, true)}</span>
          </div>,
        )
      })
    }
    if (item.action === 'Change Review Status') {
      rlt.push(
        <div key={1}>
          {item.info.status}-{item.info.requestID}
        </div>,
      )
      if (item.info.status === 'Decline') {
        rlt.push(<div key={2}>Reason: {item.info.reason}</div>)
        item.info.files?.map((file: string, index: number) => {
          rlt.push(
            <div
              key={`file-${index}`}
              className="text-[13.5px] font-semibold hover:underline cursor-pointer"
              onClick={() => openS3Document(file)}
            >
              - {getFileName(file)}
            </div>,
          )
        })
      }
    }

    return rlt
  }

  const fundingInfoFragment = useMemo(() => {
    const fundingInputs: any = fundingDisbursementInputs()
    return (
      <div className="w-full overflow-auto">
        <table className="text-[14px] rounded">
          <tbody>
            <tr>
              <td className="border px-3 py-1 font-semibold bg-gray-100" colSpan={Object.keys(fundingInputs).length}>
                Disbursement Information
              </td>
            </tr>
            <tr>
              {Object.keys(fundingInputs).map((key, index) => {
                return (
                  <td key={index} className="px-3 py-1 border">
                    {fundingInputs[key].title}
                  </td>
                )
              })}
            </tr>
            <tr>
              {Object.keys(fundingInputs).map((key, index) => {
                return (
                  <td key={index} className="px-3 py-1 border">
                    {fundingInputs[key].prefix}
                    {fundingInfo[key] || ''}
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>
    )
  }, [fundingInfo])

  return (
    <>
      <div className="relative bg-white">
        <LayoutLoading show={loading} />
        {renderRehabBudgetFragment}
        <div className="mb-4">{fundingInfoFragment}</div>
        <div className={`grid gap-4 md:grid-cols-4 grid-cols-1 mb-3`}>
          {Object.keys(inputs).map((key, index) => {
            const input: any = cloneDeep(inputs[key])
            if (input.visible === false) return null
            return (
              <div className={`input md:col-span-${input.span || 1}`} key={index}>
                <RenderInput
                  input={input}
                  Key={key}
                  onChange={onChange}
                  onBlur={onUpdate}
                  showHistory={() => onAdditionalShowHistory(key)}
                />
              </div>
            )
          })}
        </div>

        {/* What the draw is being judged against: the inspection on this loan and every reason a
            servicer would normally refuse an advance. States them, stops nothing. */}
        <DrawAdvisoryPanel advisory={advisory} loanNumber={loanNumber} className="mt-4" />

        {pendingDrawRequests.length > 0 && (
          <div className="mt-4 rounded border border-shade-blue/30 bg-shade-blue/5 px-4 py-3">
            <div className="font-variation-settings-600 text-[15px] mb-2">
              {pendingDrawRequests.length === 1
                ? '1 draw request waiting for a draw'
                : `${pendingDrawRequests.length} draw requests waiting for a draw`}
            </div>
            <div className="flex flex-col gap-2">
              {pendingDrawRequests.map((request) => {
                const approved = drawRequestApproved(request)
                return (
                  <div key={request.id} className="flex items-start justify-between gap-4 text-[14px]">
                    <div className="flex-1">
                      <span className="font-variation-settings-600">
                        {money(drawRequestTotal(request))}
                      </span>
                      <span className="opacity-80">
                        {' '}
                        from{' '}
                        {request.contactName || `${request.firstName} ${request.lastName}`.trim() || 'the borrower'}
                        {request.createdAt ? ` on ${formatDate(request.createdAt)}` : ''}
                      </span>
                      {/* Requested is what the borrower asked for; approved is what the lender
                          agreed to fund. Shown together because the gap between them is the thing
                          the borrower rings up about. */}
                      {approved !== null && (
                        <span className="ml-2 rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-[12px] whitespace-nowrap">
                          {money(approved)} approved
                        </span>
                      )}
                      {/* Whether the borrower has been told, which is the question the servicing
                          team was answering by hand until now. */}
                      {drawApprovalState(request) === 'acknowledged' && (
                        <span className="ml-2 rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-[12px] whitespace-nowrap">
                          acknowledged
                        </span>
                      )}
                      {drawApprovalState(request) === 'sent' && (
                        <span className="ml-2 rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-[12px] whitespace-nowrap">
                          sent to borrower
                        </span>
                      )}
                      <div className="opacity-70 text-[13px]">{drawRequestLines(request)}</div>
                    </div>
                    <div className="shrink-0 flex items-center gap-3">
                      <button
                        type="button"
                        className="text-shade-blue hover:underline"
                        onClick={() => setApprovingRequest(request)}
                      >
                        {approved === null ? 'Approve lines' : 'Edit approval'}
                      </button>
                      {approved !== null && (
                        <button
                          type="button"
                          className="text-shade-blue hover:underline"
                          onClick={() => setSendingApproval(request)}
                        >
                          {drawApprovalState(request) === 'approved' ? 'Send to borrower' : 'Resend'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-shade-blue hover:underline"
                        onClick={() => createDrawFromRequest(request)}
                      >
                        Create draw
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        <div className="flex justify-between items-center mt-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="flex items-center gap-1.5 rounded px-3 py-1.5 text-[14px] text-shade-blue cursor-pointer hover:bg-shade-blue/10"
              onClick={() => {
                setLinkedRequestId(null)
                formTableRef.current?.openAdd()
              }}
            >
              <PlusIcon className="w-4 h-4" />
              Add New Item
            </button>
            <div className="font-variation-settings-600 text-[18px]">
              {constructionReserve > 0 ? '- Construction Reserve' : ''}
            </div>
          </div>
          <div className="flex gap-3">
            {vendor.Afx && (
              <button
                className={`text-shade-blue hover:underline p-1 rounded w-fit flex items-center text-sm gap-1`}
                onClick={() => setAfxOpen(true)}
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                Afx Research
              </button>
            )}
            <ConstructionLienWaiver data={lienWaiverDraws} />
            <button className="hover:underline text-[14px] text-shade-blue p-2" onClick={() => onExportFile('excel')}>
              <div className="flex items-center">
                <ArrowDownTrayIcon className="w-4 h-4 mr-1" /> Export to Excel
              </div>
            </button>
            <button className="hover:underline text-[14px] text-shade-blue p-2" onClick={() => onExportFile('pdf')}>
              <div className="flex items-center">
                <ArrowDownTrayIcon className="w-4 h-4 mr-1" /> Export to Pdf
              </div>
            </button>
          </div>
        </div>
        <FormTable
          ref={formTableRef}
          showModal
          header={header}
          // Newest transaction first. `dataWithBalance` stays in the order the running balance
          // was walked in - FormTable reorders what it shows, not the rows themselves.
          defaultSortKey={CONSTRUCTION_LEDGER_DEFAULT_SORT_KEY}
          defaultSortOrder={LEDGER_DEFAULT_SORT_ORDER}
          stickyActions
          // A reserve that has been drawn on for a year runs to hundreds of rows. The running
          // balance is walked over every row before this point, so paging only changes how many
          // of them are on screen at once.
          hasPagination
          itemCountPerPage={CONSTRUCTION_LEDGER_ROWS_PER_PAGE}
          addText={'Add New Item'}
          // This page renders its own Add New Item button in the actions row (openAdd via the
          // ref), so FormTable must not render a second one.
          externalAddButton
          inputs={buildInputs}
          data={dataWithBalance}
          inputLogic={inputLogic}
          cols={3}
          onSubmit={onSubmit}
          onRemove={onRemove}
          onAddPress={onAddPress}
          renderAdditionalActions={renderAdditionalActions}
          // Only while ADDING the draw this request was opened from: editing an existing row is
          // not answering a request, and `onSubmit` only attaches the link on a new row.
          renderFormHeader={(currentId: any) =>
            currentId === 0 && linkedDrawRequest ? renderDrawRequestComparison(linkedDrawRequest) : null
          }
        />
        <div className="mt-4">
          <RenderInput
            input={inputs.comment}
            Key={'comment'}
            onChange={onChange}
            onBlur={onUpdate}
            showHistory={() => onAdditionalShowHistory('comment')}
          />
        </div>
        {interestReserve >= 0 && (
          <div className="mt-6 border-t-[3px] border-gray-400">
            <div className="mt-4">
              <RenderInput
                input={inputs.interestComment}
                Key={'interestComment'}
                onChange={onChange}
                onBlur={onUpdate}
                showHistory={() => onAdditionalShowHistory('interestComment')}
              />
            </div>
          </div>
        )}
      </div>
      {isModalOpen && (
        <ChooseDocumentsForTrustLedger
          documents={documents}
          onClose={onCloseTrustLedgerEmailModal}
          data={{
            rehabBudget,
            constructionReserve,
            data,
            ID: emailID,
          }}
        />
      )}
      {history.id && (
        <Modal
          title={`Order History - ${history.id}`}
          titleOkay=""
          isOpen={true}
          lastUpdatedAt={Date.now()}
          onClose={() => {
            setHistory({
              id: null,
              data: [],
            })
          }}
        >
          <div className="overflow-auto">
            <div className="overflow-auto shadow-md rounded">
              <table className="w-full text-[14px]">
                <thead className="font-medium text-gray-600 bg-gray-100">
                  <tr className="text-left">
                    <th className="px-4 py-2">ID</th>
                    <th className="px-4 py-2">Action</th>
                    <th className="px-4 py-2">Detail</th>
                    <th className="px-4 py-2">By/Date</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.map((item: any, index: number) => {
                    return (
                      <tr key={index} className={`${index % 2 && 'bg-gray-50'}`}>
                        <td className="px-4 py-2">{item.requestID}</td>
                        <td className="px-4 py-2">{item.action}</td>
                        <td className="px-4 py-2">{renderDetail(item)}</td>
                        <td className="px-4 py-2">
                          {item.by}
                          <br />
                          {formatTime(item.time)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}
      {isAfxOpen && <AfxModal onClose={() => setAfxOpen(false)} />}
      {feeSplitRow && (
        <DrawFeeSplitDialog
          loanNumber={loanNumber}
          row={feeSplitRow}
          onClose={() => setFeeSplitRow(null)}
          onSaved={() => {
            // Both halves move: the row's stored shares and the payouts raised from them.
            init()
            refreshPayables()
          }}
        />
      )}
      {approvingRequest && (
        <DrawRequestApprovalModal
          request={approvingRequest as any}
          onClose={() => setApprovingRequest(null)}
          // The status, the totals and the budget's approved column all come off the same
          // decision, so one reread moves every one of them.
          onSaved={() => refreshDrawRequests(initData?.budgetId || null)}
          onSend={(request: Record<string, any>) => setSendingApproval(request)}
        />
      )}
      {sendingApproval && (
        <SendDrawApprovalModal
          request={sendingApproval as any}
          loanNumber={Number(loanNumber)}
          onClose={() => setSendingApproval(null)}
          onSent={() => refreshDrawRequests(initData?.budgetId || null)}
        />
      )}
      {payPrefill && (
        <AddPayableModal
          payees={payees}
          prefill={payPrefill}
          onClose={() => setPayPrefill(null)}
          onDone={() => {
            setPayPrefill(null)
            refreshPayables()
          }}
        />
      )}
    </>
  )
}
